import type { App } from 'vue';
import { nextTick } from 'vue';
import { Toast } from '@cfx/base-mobile';
import { loadMountDocument } from '@cfx/icorki-jwx';
import { getUser } from '@dustess/itimo-core';
import MkReportSDK from '@cfx/mk-report-sdk';
import { reportReach } from '@/hooks/useReport';

export default {
  install(app: App<Element>) {
    const { $urlObj, $getAuthUserInfo, $isMp, $isQw, $isWx, $isWxMp, $materialId, $activityId, $operateEntity, $isView, $uuid, $isQwStaff, $isQwMp } = app.config.globalProperties;
    // 获取企微员工信息
    const getPlatform = () => {
      // 平台：WPN（公众号）/MINI（小程序）/QW（企微）/OTHER(第三方) 必填
      if ($isMp) return 'MINI';

      if ($isQw) return 'QW';

      if ($isWx) return 'WPN';

      return 'OTHER';
    };

    // 获取sourceId
    const getSourceId = () => {
      if (!$urlObj.deliverType) return '';
      // 处理分享&二次分享 为员工分享类型时的sourceId处理
      if ($urlObj.deliverType === 'staffShare' || $urlObj.sourceDeliverType === 'staffShare') return $urlObj.qwUid;
      return $urlObj.sourceId;
    };

    // 获取分享人类型
    const getShareObj = () => {
      const shareObj = {
        shareType: $urlObj.shareType,
        shareId: undefined,
        compatibleShareId: undefined,
        firstScene: undefined,
        clueShareId: undefined,
        secondScene: '0'
      };

      /**
       * 二级场景：（必传参数）
       * 0：表示销售分享
       * 1：表示客户转发
       * 2：表示自主访问（目前就只有用户自己搜索到我们的小程序，然后在里面访问里面访问）
       *
       * secondScene判断依据：
       *  1. 当链接上面没有isWxMp（是否为小程序）时，使用saleShare来判断
       *    saleShare：是否为销售分享的；true：是，false：否
       *    当链接上没有这个字段时，都认为是属于销售分享的
       *    当链接上有这个字段时，它的值也必定为'false'，则认为不是销售分享的，是客户转发分享的
       *  2. 当链接上面有isWxMp（是否为小程序）时，直接使用地址栏上的secondScene字段
       */

      // 如果链接上存在shareType则证明是最新分享链接 wxUser/staff/selfVisit
      if ($urlObj.shareType) {
        // 新数据处理
        if (shareObj.shareType === 'wxUser') {
          // 如果是客户转发
          shareObj.secondScene = '1';
        }
        if (shareObj.shareType === 'selfVisit') {
          // 自主访问
          shareObj.secondScene = '2';
        }
        // 如果链接上带有secondScene，则直接使用它
        if ($urlObj.secondScene) shareObj.secondScene = $urlObj.secondScene;
        if (shareObj.secondScene === '2') shareObj.shareType = 'selfVisit';
      }
      else {
        // 老数据处理
        // saleShare 该字段在新版本mk上报里面不用了，可以不用管这个字段
        shareObj.secondScene = $urlObj.saleShare === 'false' ? '1' : '0';
        if ($urlObj.shareId && $urlObj.shareId !== 'noId') {
          // 链接上存在shareId并且不等于noId则是客户分享
          shareObj.secondScene = '1';
        }
        // 当urlObj.secondScene有值时，该值从地址栏上面拿
        if ($urlObj.secondScene) shareObj.secondScene = $urlObj.secondScene;
        shareObj.shareType = shareObj.secondScene === '1' ? 'wxUser' : 'staff';
        if (shareObj.secondScene === '2') shareObj.shareType = 'selfVisit';
      }

      if (shareObj.shareType === 'staff') {
        shareObj.shareId = $urlObj.shareId || $urlObj.qwUid;
        shareObj.clueShareId = $urlObj.saleShareId;

        if (!shareObj.shareId || shareObj.shareId === 'noId') {
          // 如果是员工分享需要判断是否存在shareid否则设置未自主访问
          shareObj.shareType = 'selfVisit';
          shareObj.shareId = undefined;
          shareObj.secondScene = '2';
        }
      }

      if (shareObj.shareType !== 'staff') {
        if ($urlObj.shareType) {
          // 新数据
          shareObj.shareId = $urlObj.shareId;
          shareObj.clueShareId = $urlObj.currentShareId;
        }
        else {
          // 老数据
          shareObj.compatibleShareId = $urlObj.shareId;
          shareObj.clueShareId = $urlObj.shareId;
        }
      }

      /**
       * 一级场景：（必传参数）
       *  1. 新版本的值：一级场景:2001(文章、特赞素材)，2002(表单)，2003(H5)，2004(案例库)，2005(宣传册)，2006(视频)，2007(文件)，2008(销售名片)，2009(名片小程序) 2017（任意链接） 9999(其他)
       *  2. 老版本的值：20(文章)，21(表单)，22(H5)，9999(其他)
       */
      let firstScene = $urlObj.scene;

      if ($urlObj.version !== 'v3') {
        /**
         * 当链接上面没有v3时，表明是以前的老链接，地址上面的scene值是另外一种了，比如为：2001（文章扫码），所以需要先截取前面两位，然后重新映射为新版本的scene
         */

        // 新旧场景映射值
        const oldSceneMapNewScene = {
          20: '2001',
          21: '2002',
          22: '2003'
        };

        firstScene = firstScene.slice(0, 2);
        firstScene = oldSceneMapNewScene[firstScene as keyof typeof oldSceneMapNewScene];
      }

      shareObj.firstScene = firstScene;

      return shareObj;
    };

    app.config.globalProperties.$mkReportSDk = MkReportSDK({
      parentActivityId: $urlObj.parentActivityId,
      platform: getPlatform(),
      version: 1
    });
    app.config.globalProperties.$mkReportSDk.firstActivityId = $activityId;

    /**
     * 已上报事件处理逻辑
     * 主要是处理跳转绑定手机号
     */
    // 保存loopTime
    app.config.globalProperties.setLoopTime = () => {
      window.localStorage.setItem('loopTime', JSON.stringify({
        key: app.config.globalProperties.$activityId,
        stayTime: app.config.globalProperties.$mkReportSDk.loopParams.stayTime
      }));
    };

    // 获取loopTime
    const getLoopTime = () => {
      const stayTimeObj = JSON.parse(window.localStorage.getItem('loopTime') || '{}');
      let stayTime = 0;
      if (stayTimeObj?.key === $activityId) stayTime = stayTimeObj?.stayTime || 0;
      else window.localStorage.removeItem('loopTime');

      return stayTime;
    };

    // 获取本地保存已上报事件
    const getReportedList = () => {
      const localData = JSON.parse(window.localStorage.getItem('reportedList') || '{}');
      let list = [];
      if (localData?.key === $activityId) list = localData?.list || [];
      else window.localStorage.removeItem('reportedList');

      return list;
    };

    // 那些事件不得重复上报
    const limitList = [
      'mkFullLinkLeavePage',
      'init',
      'mkFullLinkBrowse',
      'mkFullLinkTurnLastPage',
      'mkFullLinkPlay'
    ];

    // 保存已上报事件
    const setReportedList = (event: string, str = '') => {
      if (event && limitList.includes(event)) {
        const reportedList = getReportedList();
        reportedList.push(`${event}-${str}`);
        window.localStorage.setItem('reportedList', JSON.stringify({
          key: $activityId,
          list: reportedList
        }));
      }
    };

    // 判断上报是否已执行
    const canReport = (event: string, str = '') => {
      const reportedList = getReportedList();
      let isReported = false;
      const reportEvent = `${event}-${str}`;
      if (!event || !limitList.includes(event)) return true;

      if (reportedList.includes(reportEvent)) isReported = true;

      return !isReported;
    };

    const qwUser = getUser('qw');
    const getParams = (event: string, reParams: any) => {
      const userInfo = $getAuthUserInfo();
      const par = { ...(reParams || {}) };
      const { channel } = par;
      if (channel) delete par.channel;

      const { shareType, shareId, compatibleShareId, secondScene, firstScene, clueShareId } = getShareObj();

      // 是否上报
      let isReport = true;
      // 1. 预览模式不上报
      if ($isView) isReport = false;
      // 2. 员工访问不上报（是否有B端 user 信息 & user.account === $urlObj.account ）如果是个微访问或者浏览器访问只要有B端 user 信息 & user.account === $urlObj.account
      if (qwUser?.account === $urlObj.account) isReport = false;
      // 3. 微信环境、微信小程序环境，targetType !== 'wx-unionid'，不上报
      if ((($isWx && !$isQw) || $isWxMp) && userInfo?.targetType !== 'wx-unionid') isReport = false;
      // 4. 企微小程序、企微员工（企微小程序判断的员工）不上报
      if ($isQwStaff || $isQwMp) isReport = false;
      // 5. targetId 不存在，不上报
      if (!userInfo?.targetId) isReport = false;

      return {
        params: {
          account: $urlObj.account,
          customerId: userInfo?.targetId,
          unionId: userInfo?.targetType === 'wx-unionid' ? userInfo?.targetId : '',
          event,
          operator: qwUser?.uid,
          attr: {
            deliverSceneType: $urlObj.sceneType,
            clueShareId, // 访问客户分享必传
            adId: $urlObj.adId,
            deliverType: $urlObj.sourceDeliverType || $urlObj.deliverType,
            sourceId: getSourceId(),
            contentId: $materialId, // 员工分享要传
            reportType: userInfo?.targetType || userInfo?.openId === 'wx-unionid' ? 'wx' : 'cookie', // "wx:unionId上报  customer:联系人id上报  cookie:cookie上报",
            // material(营销资料)，miniMaterial(小程序素材)，commonMaterial(普通素材)，hotMaterial(热门营销资料)，hot-article(热门好文-营销资料类型)，redPacket(红包)，dynamic(企业动态)，tempMaterial，marketingRedPacket(营销红包)，card(名片)，living(直播)，fission(裂变活动)，microSite，phone(手机号)，tel(座机)，address(通讯录)，fissionTemp(营销获客)
            sceneType: $operateEntity, // 员工分享要传
            shareType, // "分享人类型  wxUser:微信用户（存在unionId）staff:员工 非必填",
            shareId, // "分享人id 例如unionid、staffId 非必填",
            compatibleShareId,
            firstScene, // "一级场景:2001(文章)，2002(表单)，2003(H5)，2004(案例库)，2005(宣传册)，2006(视频)，2007(文件)，2008(销售名片)，2009(名片小程序),2010(营销视频),2011(企业动态),2012(H5商城),2013(小程序商城),2014(营销红包),2015(商品),9999(其他)",
            secondScene, // "二级场景,0(销售分享)，1:(客户转发),2:(自主访问)"
            carrier: {
              // "通道  1v1Message:1v1消息-0, groupMessage:群聊消息-1, qwMoments:企微朋友圈-2, wxMoments:个微朋友圈-3, wxFriends:微信好友-4, microMass:微群发-5,groupMass:群群发-6"
              channel: channel || $urlObj.channel, // 员工分享要传
              carrierType: $urlObj.carrierType, // "载体类型 例如：mkPlan:营销计划、massPlan:群发",
              carrierId: $urlObj.carrierId, // "对应载体类型的 任务id",
              extra: $urlObj.extra || $urlObj.planId // "plainId等其他的信息"
            },
            ...par
          }
        },
        isReport
      };
    };

    // 支付成功上报参数
    app.config.globalProperties.$getPaySuccessTrailParams = () => {
      const targetParams = getParams('paySuccess', {});
      return {
        ...targetParams.params,
        activityId: $uuid(),
        parentActivityId: $activityId,
        reportTime: new Date().getTime(),
        version: 1
      };
    };

    app.config.globalProperties.$report = async (event: string, reParams: any) => {
      const reportConfig = getParams(event, reParams);
      if (!reportConfig.isReport) return;
      nextTick(async () => {
        if (canReport(event || '', reportConfig?.params?.attr?.viewRatio || '')) {
          await app.config.globalProperties.$mkReportSDk.triggerEvent(event, reportConfig.params, reportConfig.isReport);
          setReportedList(event, reportConfig.params.attr.viewRatio);
        }
      });
    };

    // 判断是否处于循环上报中
    let isLoopReport = false;
    app.config.globalProperties.$reportInit = async (event: string, reParams: any) => {
      const reportConfig = getParams(event, reParams);
      if (!reportConfig.isReport) return;
      app.config.globalProperties.$mkReportSDk.firstActivityId = $activityId;
      if (canReport('init')) {
        await app.config.globalProperties.$mkReportSDk.init(reportConfig.params, reportConfig.isReport);
        setReportedList('init');
      }
      /**
       * 设置时间上报初始值
       * 如果小程序访问c端素材时，存在跳转授权手机号，需要接着上一次时间继续上报
       */
      app.config.globalProperties.$mkReportSDk.loopParams.stayTime = getLoopTime();

      app.config.globalProperties.$mkReportSDk.loopReport(reportConfig.params);
      isLoopReport = true;
      if ($isWxMp) {
        // 当在微信小程序里面访问时，需要设置新版本的wx sdk，用于向小程序传值
        loadMountDocument(['//res.wx.qq.com/open/js/jweixin-1.3.2.js']).then(() => {
          console.log('wx', window.wx);
          window.wx.miniProgram.postMessage({ data: { activityId: $urlObj.toHref ? '' : $activityId } });
        });
      }

      // 营销素材触达上报（进入页面），存在adId才进行上报
      const userInfo = $getAuthUserInfo();
      if ($urlObj?.adId && $urlObj?.sceneType !== 'conver-params') {
        const reportParams = {
          account: $urlObj.account,
          adId: $urlObj?.adId,
          activityId: $activityId,
          targetId: [userInfo?.targetId],
          targetType: $isWx ? '2' : '3' // 1:员工  2:线索  3:非微生态客户  4:群  5、外部联系人  6:企微朋友圈 7：个微朋友圈 8:c端客户
        };
        await reportReach(reportParams);
      }
    };

    // 处理浏览上报
    let viewRatioed = '';
    app.config.globalProperties.$mkFullLinkBrowse = ({ scrollTop = 0, scrollHeight = 0 }) => {
      const val = (scrollTop / scrollHeight) * 100;
      let viewRatio = '';
      if (val >= 20 && val < 40) viewRatio = '20';
      else if (val >= 40 && val < 60) viewRatio = '40';
      else if (val >= 60 && val < 80) viewRatio = '60';
      else if (val >= 80 && val < 99) viewRatio = '80';
      else if (val >= 99) viewRatio = '100';

      if (Number(viewRatioed) >= Number(viewRatio)) {
        // console.log(`已上报过 ${viewRatio}`);
      }
      else if (viewRatio) {
        viewRatioed = viewRatio;
        app.config.globalProperties.$report('mkFullLinkBrowse', { viewRatio });
      }
    };

    // 进入页面上报
    app.config.globalProperties.$enterPageFun = async (async = true) => {
      console.log('进入页面上报');
      const reportConfig = getParams('mkFullLinkVisitPage', {});
      if (!reportConfig.isReport) return;
      app.config.globalProperties.setLoopTime();
      if (canReport('mkFullLinkVisitPage')) {
        setReportedList('mkFullLinkVisitPage');
        await app.config.globalProperties.$mkReportSDk.triggerEvent('mkFullLinkVisitPage', reportConfig.params, reportConfig.isReport, async);
      }
    };

    // 离开页面上报
    app.config.globalProperties.$leavePageFun = async (async = true) => {
      Toast.loading({
        message: '请稍等',
        forbidClick: true
      });
      const reportConfig = getParams('mkFullLinkLeavePage', {});
      if (!reportConfig.isReport) return;
      app.config.globalProperties.setLoopTime();
      if (canReport('mkFullLinkLeavePage')) {
        setReportedList('mkFullLinkLeavePage');
        await app.config.globalProperties.$mkReportSDk.triggerEvent('mkFullLinkLeavePage', reportConfig.params, reportConfig.isReport, async);
      }
      Toast.clear();
      app.config.globalProperties.$mkReportSDk.handleClearLoopReport();
    };

    // 以下时离开页面上报逻辑，没什么用 ⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇
    const unloadCallback = () => {
      app.config.globalProperties.$leavePageFun(false);
      window.removeEventListener('beforeunload', unloadCallback);
    };

    window.addEventListener('beforeunload', unloadCallback);
    // 以上时离家页面上报逻辑 ⬆⬆⬆⬆⬆⬆⬆

    // 页面隐藏时处理时间上报
    document.addEventListener('visibilitychange', () => {
      console.log('document.hidden: ', document.hidden);
      // 当页面隐藏时，暂停上报
      if (document.hidden) {
        app.config.globalProperties.$mkReportSDk.handleClearLoopReport();
      }
      else if (isLoopReport) {
        // 当页面显示时重新上报
        const reportConfig = getParams('', {});
        if (!reportConfig.isReport) return;
        app.config.globalProperties.$mkReportSDk.loopReport(reportConfig.params);
      }
    });
  }
};
